import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Calculator, TrendingDown, TrendingUp } from "lucide-react";

interface TrueUpCalculationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TrueUpCalculationDialog = ({
  open,
  onOpenChange,
}: TrueUpCalculationDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-purple-900 flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            TRUE-UP CALCULATION LOGIC
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-2">
            True-up billing reconciles the difference between estimated
            consumption billed previously and actual consumption.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Calculation Steps */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              Calculation Steps:
            </h3>

            <div className="space-y-4">
              {[
                {
                  step: 1,
                  title: "Determine Estimated Consumption",
                  desc: "Consumption that was billed earlier based on an estimate.",
                },
                {
                  step: 2,
                  title: "Calculate Actual Consumption",
                  desc: "Actual consumption derived from meter readings.",
                  formula:
                    "Actual Consumption = Current Reading - Previous Reading",
                },
                {
                  step: 3,
                  title: "Compare and Adjust",
                  desc: "Difference between actual and estimated consumption.",
                  formula:
                    "Difference = Actual Consumption - Estimated Consumption",
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-semibold">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">{item.desc}</p>
                    {item.formula && (
                      <div className="bg-gray-50 p-3 rounded text-xs font-mono">
                        {item.formula}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Under-Billing */}
          <div className="border border-green-200 rounded-lg p-4 bg-green-50">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <h4 className="font-semibold text-green-900">
                Under-Billing (Actual &gt; Estimated)
              </h4>
            </div>
            <p className="text-sm text-green-900">
              Additional charges are applied to recover the difference.
            </p>
          </div>

          {/* Over-Billing */}
          <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="h-4 w-4 text-orange-600" />
              <h4 className="font-semibold text-orange-900">
                Over-Billing (Actual &lt; Estimated)
              </h4>
            </div>
            <p className="text-sm text-orange-900">
              Credits or refunds are applied for excess billing.
            </p>
          </div>

          {/* Key Takeaway */}
          <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r">
            <p className="text-sm text-blue-900">
              <strong>Key Takeaway:</strong> True-Up billing ensures fairness
              for both consumers and utilities by correcting estimation errors.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
